import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    MenuItem,
    Card,
    CardActionArea,
    CardContent,
    CardMedia,
    FormControl,
    InputLabel,
    Select,
    styled
} from '@mui/material';
import { ChevronDown } from 'lucide-react';
import ActivityBar from '../ActivityBar/ActivityBar';
import SectionSlider from '../SectionSlider/SectionSlider';
import { sections, sectionPaths } from '../../../constants/sectionData';
import profileImg from '../../../assets/SVGs/profile_pic.svg';

import { apiService } from '../../../api/apiService';
import GlobalPoliciesFileViewPopup from '../Popup/GlobalPoliciesFileViewPopup';
import { getFileImagePath } from '../utils/fileUtils';
import UserFileViewPopup from '../Popup/UserFileViewPopup';
import { useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import useFilter, { FilterValues } from '../../../hooks/useFilter';
import FilterComponent from '../utils/FilterUtils/FilterUtils';



interface DocumentData {
    id: number;
    doc_path: string;
    status: string;
    manager_id: string;
    management: string;
    function_l1: string;
    function_l2: string;
    function_l3: string;
    created_at: string;
    updated_at: string;
    reason_to_approve: string | null;
    reason_to_reject: string | null;
    reason_to_revert: string | null;
    file_size: any;
    file_name: any;
    created_by: any;

}

interface UserSectionViewProps {
    fetchAllFilesApi?: string;
    fetchFileViewApi?: any;
    sectionTitle?: any;
    highlightSection?: any;
    verticalTabs?: { label: string; value: string }[];
    onFilterChange?: (filters: FilterValues) => void;
    levels?: number;
}

const StyledMenuItem = styled(MenuItem)({
    color: 'black', // Set the text color to black
    '&:hover': {
        backgroundColor: '#f0f0f0', // Optional: hover background color
    },
});




export default function UserSection({ fetchAllFilesApi, fetchFileViewApi, sectionTitle, highlightSection, verticalTabs = [], levels }: UserSectionViewProps) {
    const { instance } = useMsal();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
 
    const management = localStorage.getItem("management") || "Prague & India GCC";
    const [verticalTabValue, setVerticalTabValue] = useState("a");
    const [routineData, setRoutineData] = useState<any[]>([]);

    const [selectedDocument, setSelectedDocument] = useState<DocumentData | null>(null);


    const handleCardClick = (document: DocumentData) => {
        setSelectedDocument(document); // Set the clicked document
    };

    const fetchRoutineData = async (filters?: FilterValues) => {
        setIsLoading(true);
        try {
            let response;
            if (fetchAllFilesApi === '/management/process_map_by_management') {
                response = await apiService(
                    `${fetchAllFilesApi}/${management}`,
                    "GET",
                    undefined,
                    undefined,
                    instance as PublicClientApplication
                );
            } else {
                response = await apiService(
                    fetchAllFilesApi || '',
                    "GET",
                    {
                        management: management,
                        section_name: verticalTabs.find((tab) => tab.value === verticalTabValue)?.label,
                        ...(filters || {})
                    },
                    undefined,
                    instance as PublicClientApplication
                );
            }

            if (response.data) {
                setRoutineData(response.data);
            }
        } catch (error) {
            console.error("Error fetching routine data:", error);
            setRoutineData([]);

            if (error instanceof Error) {
                setError(error.message || "An error occurred");
            } else {
                setError("An error occurred");
            }

            if (error instanceof Error && (error as any).response && (error as any).response.status === 401) {
                navigate('/login');
            }
        } finally {
            setIsLoading(false);
        }
    };

const { filterValues, applyFilter, resetFilter } = useFilter();

    const handleApplyFilter = (filters: FilterValues) => {
        const newFilters = applyFilter(filters);
        fetchRoutineData({
            ...newFilters,
            function_l1: newFilters.function_l1 || '',
            function_l2: newFilters.function_l2 || '',
            function_l3: newFilters.function_l3 || '',
            function_l4: newFilters.function_l4 || '',
        });
    };

      

    useEffect(() => {
        const resetAndFetch = async () => {
          const newFilters = resetFilter(); // get reset filters
          await fetchRoutineData({
            ...newFilters,
            function_l1: newFilters.function_l1 || '',
            function_l2: newFilters.function_l2 || '',
            function_l3: newFilters.function_l3 || '',
            function_l4: newFilters.function_l4 || '',
          });
        };
      
        resetAndFetch();
      }, [verticalTabValue, sectionTitle]);
      


    const breadcrumbs = [
        <Link key="1" color="#000" to="/">
                   Home
               </Link>,
               <Link key="2" color="#000" to="/operations/management">
                   Evidence Upload
               </Link>,
               <Link key="3" color="#000" to="/operations/management">
                   Management
               </Link>,
        <Typography key="4" sx={{ fontFamily: 'Avantt' }}>
            {management}
        </Typography>,
    ];



    return (
        <>
            {/* Activity Bar */}
            <ActivityBar breadcrumbs={breadcrumbs} />

            <Box sx={{ width: '100%' }}>
                {/* Section Slider */}
                <SectionSlider
                    sections={sections}
                    sectionPaths={sectionPaths}
                    highlightSection={highlightSection}
                    highlightColor="#D7A833"
                    defaultColor="#666"
                />

                {/* MICS Action Plan Map Section */}
                <Box sx={{ mx: 3, borderRadius: '11px' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 3,
                            backgroundColor: '#fff',
                            padding: 2,
                            borderRadius: '11px',
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{ fontFamily: 'Avantt', fontWeight: '600', alignItems: 'center', display: 'flex', gap: '8px' }}
                        >
                            {sectionTitle}
                        </Typography>

                        <FilterComponent
                            levels={levels ?? 0}
                            onApplyFilter={handleApplyFilter}
                            initialValues={filterValues}
                        />
                    </Box>




                    {isLoading ? (
                        <Typography sx={{textAlign:"center", mt:"4"}}>Loading...</Typography>
                    ) : error ? (
                        <Typography color="error">{error}</Typography>
                    ) : (
                        <>
                            {routineData.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start',gap: '10px',marginBottom:'20px' }}>
                                    {routineData.map((document) => (
                                        <Card
                                            sx={{ maxWidth:'24% !important', width:"24% !important", marginTop: '10px', borderRadius: '11px' }}
                                            key={document.id}
                                        >
                                            <CardActionArea onClick={() => handleCardClick(document)}>
                                                <CardMedia
                                                    component="img"
                                                    image={getFileImagePath(document.doc_path)}
                                                    sx={{ padding: '16px', borderRadius: '11px', height: '100px', width: '100px' }}
                                                />
                                                <CardContent>
                                                    <Typography sx={{
  fontSize: "14px",
  fontWeight: "600",
  color: "#000",
  width: '90%',  
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
}} variant="body1">
                                                        {document.file_name.split('_').slice(5).join('_')}
                                                    </Typography>
                                                    <Box
                                                        display="flex"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        sx={{ paddingTop: '10px', paddingBottom: '10px' }}
                                                        gap="20px"
                                                    >
                                                        <Typography variant="body2" sx={{ color: 'text.secondary',fontSize:"14px" }}>
                                                            Created on {new Date(document.created_at).toLocaleDateString()}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: 'text.secondary',fontSize:"14px" }}>
                                                            {document.file_size}
                                                        </Typography>
                                                    </Box>

                                                    {/* <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            display: 'flex',
                                                            gap: '4px',
                                                            pt: '10px',
                                                        }}
                                                    >
                                                        You opened:{' '}
                                                        <Typography variant="body2" sx={{ color: 'blue' }}>
                                                            Just Now
                                                        </Typography>
                                                    </Typography> */}

                                                    <Box sx={{ paddingTop: '10px', display: 'flex', gap: '10px' }}>
                                                        {/* <Box>
                                                            <img src={profileImg} width={35} height={35} alt="" />
                                                        </Box> */}
                                                        <Box sx={{ display: 'grid', gap: '1px' }}>
                                                            <Typography sx={{ color: '#1e1e1e80', fontSize: '11px' }}>Created by</Typography>
                                                            <Typography sx={{ fontSize: '11px', fontWeight: 'bold' }}>{document.created_by}</Typography>
                                                        </Box>
                                                    </Box>
                                                </CardContent>
                                            </CardActionArea>
                                        </Card>
                                    ))}

                                    {selectedDocument && (
                                        <UserFileViewPopup
                                            onClose={() => setSelectedDocument(null)}
                                            open={true}
                                            name={selectedDocument.file_name}
                                            id={selectedDocument.id}
                                            functionl1={selectedDocument.function_l1}
                                            date={selectedDocument.created_at}
                                            sectionTitle={sectionTitle}
                                            apiPath={`${fetchFileViewApi}/${selectedDocument.id}`}
                                        />
                                    )}
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    <Typography variant="h6" sx={{ fontFamily: 'Avantt' }}>
                                        No data available
                                    </Typography>
                                </Box>
                            )}
                        </>

                    )}
                </Box>
            </Box>
        </>
    );
}
